import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Store } from 'types/store';
import trackEvent from 'services/AnalyticsService';
import appleStore from 'assets/images/landing/apple_store.svg';
import androidStore from 'assets/images/landing/android_store_smaller.svg';
import Modal from 'react-bootstrap/Modal';
import QRCode from './AppMerchQRCode.svg';
import Warning from './Warning.svg';
import SMS from './SMS.svg';
import Promotion from './Promotion.svg';
import styles from './AppMerchandising.module.scss';

const STORAGE_KEY = 'appMerchandisingPopupLastShown';
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000; // 30 days

export const showAppMerchandisingPopup = () => {
  const lastShown = Number(localStorage.getItem(STORAGE_KEY) || 0);

  if (!lastShown) {
    return true;
  }

  const timePassed = Date.now() - Number(lastShown);

  return timePassed > THIRTY_DAYS;
}

const AppMerchandising = () => {
  const popupRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const custInfo = useSelector((state: Store) => state.customer.custInfo);

  const setClosedDate = () => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
  };

  const handleClose = () => {
    trackEvent('MyRAC_AppMerchandising_PopupClosed');
    setClosedDate();
    setIsOpen(false);
  };

  useEffect(() => {
    if (!showAppMerchandisingPopup()) {
      return;
    }

    // Timeout workaround to wait till other popup data is set
    const timeout = setTimeout(() => {
      setIsOpen(true);
    }, 2000);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (isOpen) {
      trackEvent('MyRAC_AppMerchandising_PopupDisplayed');
      popupRef.current?.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Modal
      style={{ maxWidth: 'auto' }}
      show={isOpen}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Body className="p-0">
        <div className={styles.popup}>
          <button
            className={styles.closeButton}
            type="button"
            onClick={handleClose}
            aria-label="Close"
            tabIndex={0}
          >&#215;
          </button>
          <div className="modal-body p-0">
            <div className={styles.border}>
              <div className={styles.body}>
                <h3 className={styles.header}>Download the <b>App</b></h3>
                <p className={styles.subheader}>Hi {custInfo.Forename}, your app benefits are waiting</p>
                <p className={styles.paragraph}>
                  Scan this code with your phone<br />
                  to download and login.
                </p>
                <p className={styles.paragraphMobile}>
                  Tap below to download.
                </p>
                <div className={styles.qrcode}>
                  <img src={QRCode} alt="QR Code for App" />
                </div>
                <div className={styles.mobileButtons}>
                  <a
                    href="https://apps.apple.com/gb/app/myrac/id350330435"
                    onClick={() => {
                      trackEvent('MyRAC_AppMerchandising_AppleStore');
                      setClosedDate();
                    }}
                  >
                    <img className="app-store-link" src={appleStore} alt="apple store" />
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=uk.co.rac.roadside"
                    onClick={() => {
                      trackEvent('MyRAC_AppMerchandising_PlayStore');
                      setClosedDate();
                    }}
                  >
                    <img className="app-store-link" src={androidStore} alt="android store" />
                  </a>
                </div>
                <div className={styles.footer}>
                  <p><i>With the app you can...</i></p>
                  <div className={styles.benefits}>
                    <div><img src={Warning} alt="Warning icon" /> Faster breakdown help</div>
                    <div><img src={SMS} alt="SMS icon" /> Live assistance tracking</div>
                    <div><img src={Promotion} alt="Promotion icon" /> Exclusive member benefits</div>
                  </div>
                  <p><i>....plus lots more.</i></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>

  );
};

export default AppMerchandising;
